import { PageHeader, Section } from "@/components/site/PageChrome";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { waLink, WHATSAPP_DISPLAY } from "@/components/site/FloatingWhatsApp";
import { MessageCircle } from "lucide-react";
import { HOME } from "@/constants/testIds";

const groups = [
  {
    title: "Getting Started",
    faqs: [
      {
        q: "What is Time2Cash?",
        a: "Time2Cash is India's hyperlocal community platform that connects local tasks, jobs, businesses, marketplace listings, blood requests, and many other city services in one app.",
      },
      {
        q: "How do I create an account?",
        a: "Simply download the app, sign in using your mobile number or Google account, complete your profile, and start exploring nearby opportunities.",
      },
      {
        q: "Is Time2Cash free to use?",
        a: "Yes. Time2Cash is free to download and use. Some premium features and business services may require credits or subscription plans.",
      },
    ],
  },

  {
    title: "Local Tasks & Jobs",
    faqs: [
      {
        q: "How do I post a local task?",
        a: "Open the Local Tasks section, enter your requirement, budget, and location, then publish it for nearby people to respond.",
      },
      {
        q: "Can I find both part-time and full-time jobs?",
        a: "Yes. Time2Cash lets you discover nearby part-time and full-time jobs posted by local businesses and individuals.",
      },
      {
        q: "Can businesses post job openings?",
        a: "Yes. Businesses can post job vacancies and connect directly with local job seekers.",
      },
    ],
  },

  {
    title: "Businesses & Marketplace",
    faqs: [
      {
        q: "How can I register my business?",
        a: "Create a Business Profile, add your services, business details, contact information, and start receiving enquiries from nearby customers.",
      },
      {
        q: "Can I buy and sell products?",
        a: "Yes. The Marketplace allows users to buy, sell, or rent products directly without any middlemen.",
      },
      {
        q: "Does Time2Cash charge commission?",
        a: "No. Time2Cash does not charge commission on transactions between users. Buyers and sellers connect directly.",
      },
    ],
  },

  {
    title: "Blood Requests & Safety",
    faqs: [
      {
        q: "How do Blood Requests work?",
        a: "Users can post emergency blood requirements, allowing nearby donors to connect quickly during critical situations.",
      },
      {
        q: "Is the SOS feature available?",
        a: "The SOS Safety feature is currently under development and is planned to launch in 2027 with live emergency location sharing.",
      },
    ],
  },

  {
    title: "Wallet & Credits",
    faqs: [
      {
        q: "What are Credits used for?",
        a: "Credits are used for selected premium actions such as posting certain listings, unlocking opportunities, and accessing additional platform features.",
      },
      {
        q: "Can I purchase Credits?",
        a: "Yes. Credits can be purchased directly from the Wallet section inside the app whenever required.",
      },
    ],
  },

  {
    title: "Support",
    faqs: [
      {
        q: "How can I contact Time2Cash?",
        a: "You can contact us through the Contact page, WhatsApp support, or email us at hello@time2cash.co.",
      },
      {
        q: "Which cities are currently available?",
        a: "Time2Cash is launching in Anantapur first, with expansion to Kurnool planned soon. More cities will be added gradually.",
      },
    ],
  },
];

export default function FAQPage() {
  return (
    <>
      <PageHeader
        eyebrow="Help Center / FAQ"
        title="How can we help?"
        subtitle="Answers to common questions about Time2Cash — wallet, shops, marketplace, jobs, safety, and more."
      />
      <Section>
        <div className="max-w-3xl mx-auto space-y-10">
          {groups.map((g, gi) => (
            <div key={g.title}>
              <h2 className="text-2xl font-bold text-[#1A1918]">{g.title}</h2>
              <Accordion type="single" collapsible className="mt-4 space-y-3" data-testid={gi === 0 ? HOME.faqAccordion : undefined}>
                {g.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`${gi}-${i}`} className="rounded-2xl border border-[#EBE8E3] bg-white px-5 data-[state=open]:border-[#FF5A00] transition-colors">
                    <AccordionTrigger className="text-left font-bold text-[#1A1918] hover:no-underline text-base">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-[#4A4846] leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>

        <div className="max-w-3xl mx-auto mt-12 rounded-3xl bg-[#25D366] text-white p-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Still need help?</h3>
              <p className="text-white/90 text-sm">Chat with our support team on WhatsApp — {WHATSAPP_DISPLAY}</p>
            </div>
          </div>
          <a
            href={waLink("Hi Time2Cash! I have a question and need support.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="faq-whatsapp"
            className="rounded-full bg-white text-[#075E54] px-6 py-3 font-semibold hover:bg-white/90 transition whitespace-nowrap"
          >
            Chat on WhatsApp
          </a>
        </div>
      </Section>
    </>
  );
}
